import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { createStore, get, set, del, keys, clear } from 'idb-keyval';
import type { Query } from '@tanstack/react-query';
import { AuthTokenService } from './auth-token-service';

const DB_NAME = 'getfacade-query-cache';
const STORE_NAME = 'v1';
// Bumped when a snapshot written by older code must not be hydrated at all.
// v2: snapshots up to 2026-08-06 carry catalogue answers that predate fields
// the current code reads (`website_url` on a manufacturer). They are excluded
// from new snapshots, but an existing one would still be restored once — and
// the catalogue's 24h staleTime means that one restore is what the user sees
// for the rest of the day. Renaming the entry drops them instead.
const CACHE_KEY = 'queries-v2';
const LEGACY_CACHE_KEYS = ['queries'];
const ANON_SCOPE = 'anon';

const idbStore =
  typeof indexedDB !== 'undefined' ? createStore(DB_NAME, STORE_NAME) : null;

// Namespace every IndexedDB entry by the current account. Without this a
// different user can hydrate the previous user's persisted queries
// (`['projects','detail',id]`, albums, estimates…) whenever a clean logout
// didn't run — a silently swapped token, an interrupted logout, or a crash
// between login and the identity-change wipe. The id is read synchronously from
// localStorage, so the very first restore at cold boot already lands in the
// correct per-account bucket; an anonymous boot uses a dedicated `anon` bucket.
function scopedKeyFor(userId: string | null, key: string): string {
  return `${userId ?? ANON_SCOPE}::${key}`;
}

function scopedKey(key: string): string {
  return scopedKeyFor(AuthTokenService.getUserIdSync(), key);
}

// Sweep the entries older code wrote. Best-effort and fire-and-forget: it runs
// once per page load, and a failure only leaves dead bytes in IndexedDB.
if (idbStore) {
  void keys(idbStore)
    .then((stored) =>
      Promise.all(
        stored
          .filter(
            (key) =>
              typeof key === 'string' &&
              LEGACY_CACHE_KEYS.some((legacy) => key.endsWith(`::${legacy}`))
          )
          .map((key) => del(key, idbStore))
      )
    )
    .catch(() => undefined);
}

const asyncStorage = {
  getItem: async (key: string) => {
    if (!idbStore) return null;
    const value = await get<string>(scopedKey(key), idbStore);
    return value ?? null;
  },
  setItem: async (key: string, value: string) => {
    if (!idbStore) return;
    await set(scopedKey(key), value, idbStore);
  },
  removeItem: async (key: string) => {
    if (!idbStore) return;
    await del(scopedKey(key), idbStore);
  },
};

export const queryPersister = createAsyncStoragePersister({
  storage: asyncStorage,
  key: CACHE_KEY,
  throttleTime: 1000,
});

/**
 * Query keys that must NEVER be persisted to disk.
 * Auth/billing/subscription/payments change often and are security-sensitive —
 * "fail loudly" rules out stale reads from IDB. Job queue and history are
 * session-scoped realtime state, not a cache to seed the next reload.
 */
const VOLATILE_ROOTS = new Set([
  'auth',
  'user',
  'profile',
  // Sign-in state flips mid-session (a method added, accounts merged) right
  // before a full reload; a throttled write left `requires_sign_in_method:
  // true` on disk, so the required overlay flashed after the reload and its
  // section consumed the one-shot merge message.
  'sign-in',
  'subscriptions',
  'subscription',
  'payments',
  'payment',
  'job-history',
  'jobs',
  'queue',
  // The product catalogue. It looks like the ideal thing to persist — a
  // product's name, code and colour never change under us — but the catalogue
  // GROWS every week (new manufacturers, new lines, new fields on an existing
  // row), and its queries run at a 24h staleTime, so a persisted answer is not
  // refetched while it is served. Together that is a snapshot on disk that
  // outlives the code reading it: after `website_url` was added to the
  // manufacturer response, a browser that had opened Paints the day before kept
  // hydrating the field-less answer and showed no link to the maker's site,
  // through reloads and a browser cache clear (both leave IndexedDB alone),
  // while Siding — first opened after the change — was fine. The `buster` is no
  // safety net here: it is a constant on any build with no version set.
  //
  // In-memory caching still holds for the whole session, so walking the tree
  // costs nothing; only a page load asks the server again.
  'catalog',
  // Same shape of data, same reason: resolved brand-selection labels.
  'brand-selections',
  // Storefront catalogs. They look static — pack copy, token counts, prices —
  // but every item also carries `availability`, which is PER-USER state
  // (`high_balance`, `daily_limit`, `account_blocked`) recomputed on each
  // request from the token balance and the 24h spend. Persisted, that verdict
  // outlives the balance it was computed from: a 7-day IDB snapshot kept
  // telling a buyer "you have a high token balance, spend some first" after
  // their balance had been emptied to 46 (production, 2026-09-09), through
  // reloads, because a hydrated answer is served while the background refetch
  // is still in flight. Pricing/copy still cache in memory for the session;
  // only a page load asks the server again, which is exactly what must happen
  // before we refuse someone's money.
  'store-catalog',
  'token-packages',
  'passes',
]);

export function shouldPersistQuery(query: Query): boolean {
  const root = query.queryKey[0];
  if (typeof root !== 'string') return false;
  if (VOLATILE_ROOTS.has(root)) return false;
  // Only persist queries that have actually loaded data successfully.
  return query.state.status === 'success';
}

/**
 * Drop the persisted cache. With a `userId` (logout / identity-change) we wipe
 * just that account's bucket (plus the anonymous one, in case pre-login queries
 * landed there) and leave other accounts' buckets intact — per-account scoping
 * already prevents cross-user hydration, so there's no need to nuke everyone.
 * With no argument we clear the whole store (defensive full wipe, e.g. a
 * version bust).
 */
export async function clearPersistedQueryCache(
  userId?: string | null
): Promise<void> {
  if (!idbStore) return;
  if (userId === undefined) {
    await clear(idbStore);
    return;
  }
  await del(scopedKeyFor(userId, CACHE_KEY), idbStore);
  await del(scopedKeyFor(ANON_SCOPE, CACHE_KEY), idbStore);
}
